import { useLocation } from 'react-router-dom';
import { useAppDispatch } from '@hooks/reduxHooks';
import { $api } from '@service/RequestService';
import { timeStampToDate } from '@utils/index';
import { useQuery } from 'react-query';

import { IAIAgent } from '../models';
import { setBody, setEdges, setInitialNodes, setIsUpdate, setNodes } from '../store';

export function useGetAIAgent() {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const segments = location.pathname.split('/');
  const agentId = segments[2];

  return useQuery<IAIAgent>({
    queryKey: ['one-ai-agent', agentId],
    queryFn: async () => {
      const res = await $api.get(`/v1/chat-flow/${agentId}?original=true`);

      const dataCopy = res?.data?.data as IAIAgent;

      const data: IAIAgent = dataCopy
        ? {
            ...dataCopy,
            createdDate: timeStampToDate({ date: dataCopy?.createdDate, timeStamp: dataCopy?.createdTimestamp }),
            updatedDate: timeStampToDate({ date: dataCopy?.updatedDate, timeStamp: dataCopy?.updatedTimestamp })
          }
        : dataCopy;

      const { name, description, id, type, dataType } = data;
      const nodes = (data?.graph?.blocks || [])?.map(item => ({
        id: String(item?.id),
        oldId: String(item?.id),
        position: {
          x: item?.x,
          y: item?.y
        },
        data: {
          name: item?.name,
          description: item?.description,
          ...(item?.type === 'QUESTION_CLASSIFIER' && { options: item?.options }),
          json: item?.dataJson
            ? {
                ...JSON.parse(item?.dataJson),
                inputType: item?.inputType,
                tokenLimit: item?.tokenLimit,
                ai: item?.ai,
                useOldConversation: item?.useOldConversation
              }
            : {}
        },
        type: item?.type
      }));

      const edges = (data?.graph?.connections || [])?.map(item => {
        const sourceNode = nodes.find(n => n.id === String(item?.source));

        let optionIndex: number | null = null;

        if (
          sourceNode?.type === 'QUESTION_CLASSIFIER' &&
          Array.isArray(sourceNode.data?.options) &&
          item?.option !== null
        ) {
          optionIndex = sourceNode.data.options.findIndex((opt: any) => opt.id === item?.option);
          if (optionIndex === -1) optionIndex = null;
        }

        const newEdges = {
          id: item?.id,
          source: String(item?.source),
          target: String(item?.target),
          ...(item?.option ? { option: item?.option } : undefined),
          ...(optionIndex !== null ? { sourceHandle: `source_${optionIndex}` } : {})
        };

        return newEdges;
      });

      dispatch(setBody({ name, description, id, dataType: dataType || 'LEAD', type }));
      dispatch(setNodes(() => nodes));
      dispatch(setInitialNodes(() => nodes));
      dispatch(setEdges(() => (edges as any) || []));
      dispatch(setIsUpdate(false));

      return data;
    },
    enabled: !!agentId && agentId !== 'new',
    retry: false,
    refetchOnWindowFocus: false
  });
}
